import axios from "axios"
import moment from "moment"
import {
  buildAccommodationItem,
  trackSelectItem,
  trackViewItemList,
} from "../../custom/holiday-park/analytics/holidayParkDataLayer"

const MAX_PEOPLE = 6
const MIN_ADULTS = 1
const DATE_FLEXIBILITY_PATTERN = /^plus_minus_(\d+)$/
const LIST_NAME_SEARCH_RESULTS = "Search results"
const LIST_NAME_ALTERNATIVE_DATES = "Alternative dates"
const LIST_NAME_ACCOMMODATION_OPTIONS = "Accommodation options"
const LIST_NAME_ACCOMMODATION_ALTERNATIVES = "Accommodation alternatives"

class HolidayParkSearchResults {
  constructor(container) {
    this.container = container
    this.isSearchResultsPage = container.id === "search-results"
    this.isAccommodationPage = container.hasAttribute("data-holiday-park-accommodation-search")
    this.propertyList = this.findPropertyList()
    this.propertyCardContainer = this.findPropertyCardContainer()
    this.loadingSpinner = this.findLoadingSpinner()
    this.noResultsMessage = this.findNoResultsMessage()
    this.alternativeResultsMessage = this.findAlternativeResultsMessage()
    this.exactResultsSection = this.findExactResultsSection()
    this.exactResultsList = this.findExactResultsList()
    this.partySummary = this.findOrCreatePartySummary()
    this.bookingType = container.querySelector("#booking-type")
    this.availabilitySearchBar = this.findSearchBar()
    this.currentSearchFields = {}
    this.alternativeScrollFrame = null
    this.latestSearchRequestId = 0
    this.activeSearchController = null
    this.accommodationPageData = this.getAccommodationPageData()
    this.linkedGradeCodes = this.accommodationPageData.linked_grade_codes ?? []
    this.linkedPropertiesByGradeCode = new Map(
      (this.accommodationPageData.linked_properties ?? []).map((property) => [property.grade_code, property])
    )
    this.alternativeAccommodations = this.accommodationPageData.alternative_accommodations ?? {}

    if ((!this.propertyList && !this.isAccommodationPage) || !this.availabilitySearchBar) {
      this.hideLoadingSpinner()
      return
    }

    this.bindAnalytics()
    this.bindSearch()

    if (this.isSearchResultsPage) {
      this.fetchNewResults(this.getFieldsFromQueryString())
    }
  }

  findPropertyList() {
    return this.container.querySelector(".holiday-park-search-results__property-list")
      ?? this.container.querySelector("#property-list")
  }

  findPropertyCardContainer() {
    return this.propertyList?.querySelector(".accommodation-holiday-park-list") ?? this.propertyList
  }

  findLoadingSpinner() {
    return this.container.querySelector(".holiday-park-search-results__spinner")
      ?? this.container.querySelector("#results-loading-spinner")
  }

  findNoResultsMessage() {
    return this.container.querySelector(".holiday-park-search-results__no-results")
      ?? this.container.querySelector("#no-results-found-message")
  }

  findAlternativeResultsMessage() {
    return this.container.querySelector(".holiday-park-search-results__alternatives")
  }

  findExactResultsSection() {
    return this.container.querySelector("[data-accommodation-exact-results]")
  }

  findExactResultsList() {
    return this.container.querySelector("[data-accommodation-exact-results-list]")
  }

  getAccommodationPageData() {
    const payload = this.container.querySelector("[data-accommodation-search-data]")?.textContent

    if (!payload) {
      return {}
    }

    try {
      return JSON.parse(payload)
    } catch (error) {
      console.error("Failed to parse accommodation page search data", error)
      return {}
    }
  }

  findOrCreatePartySummary() {
    const existingSummary = this.container.querySelector("[data-search-party-summary]")

    if (existingSummary) {
      return existingSummary
    }

    const summary = document.createElement("div")
    const referenceElement = this.loadingSpinner
      ?? this.noResultsMessage
      ?? this.alternativeResultsMessage
      ?? this.propertyList

    summary.className = "hidden holiday-park-search-results__party-summary"
    summary.dataset.searchPartySummary = "true"

    if (referenceElement?.parentNode) {
      referenceElement.insertAdjacentElement("afterend", summary)
    }

    return summary
  }

  findSearchBar() {
    if (this.isAccommodationPage) {
      return this.container.querySelector("[data-accommodation-search-panel] > form")
        ?? this.container.querySelector("form")
    }

    if (this.isSearchResultsPage) {
      const fixedForm = document.querySelector(".searchBarContainer.on-page > form")

      if (fixedForm) {
        return fixedForm
      }
    }

    const desktopForm = this.container.querySelector(".searchBarContainer > form")
    const mobileForm = this.container.querySelector(".searchBarMobile form")

    return desktopForm || mobileForm
  }

  bindSearch() {
    this.availabilitySearchBar.addEventListener("submit", (event) => this.handleSearchEvent(event), { capture: true })

    const submitButton = this.availabilitySearchBar.querySelector(".search-bar-submit, button[type='submit']")
    if (submitButton) {
      submitButton.type = "button"
      submitButton.addEventListener("click", (event) => this.handleSearchEvent(event), { capture: true })
    }

    if (this.isSearchResultsPage) {
      window.addEventListener("popstate", () => {
        this.fetchNewResults(this.getFieldsFromQueryString())
      })
    }
  }

  bindAnalytics() {
    this.container.addEventListener("click", (event) => this.onTrackedSelectItem(event))
  }

  onTrackedSelectItem(event) {
    const link = event.target.closest("[data-ga4-select-item]")

    if (!link || !this.container.contains(link)) {
      return
    }

    try {
      trackSelectItem({
        listName: link.dataset.ga4SelectItemList,
        item: JSON.parse(link.dataset.ga4SelectItem),
        index: parseInt(link.dataset.ga4SelectItemIndex ?? "1", 10) || 1,
      })
    } catch (error) {
      console.error("Failed to parse holiday park select_item payload", error)
    }
  }

  handleSearchEvent(event) {
    const submitter = event.submitter ?? event.currentTarget

    if (submitter?.classList?.contains("button-link-2")) {
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    this.handleSearchSubmit()
  }

  handleSearchSubmit() {
    if (!this.availabilitySearchBar.checkValidity()) {
      this.availabilitySearchBar.reportValidity()
      return
    }

    const searchFields = this.getFieldsFromForm()

    this.syncSearchFormFields(searchFields)
    this.updateSearchUrl(searchFields)
    this.fetchNewResults(searchFields)
  }

  async fetchNewResults(fields = null) {
    if (this.isAccommodationPage) {
      await this.fetchAccommodationPageResults(fields)
      return
    }

    const requestId = ++this.latestSearchRequestId
    this.cancelActiveSearch()
    this.activeSearchController = new AbortController()

    this.hidePropertyList()
    this.hideNoResultsMessage()
    this.hideAlternativeResultsMessage()
    this.showLoadingSpinner()

    const searchFields = this.withDefaultFields(fields ?? this.getFieldsFromForm())
    this.syncSearchFormFields(searchFields)
    this.updateSearchUrl(searchFields)
    this.currentSearchFields = searchFields
    this.updatePartySummary(searchFields)
    this.setSearchBusy(true)
    const results = await this.getResultsFromBackend(searchFields, this.activeSearchController.signal)

    if (requestId !== this.latestSearchRequestId) {
      return
    }

    const hasExactResults = this.showResults(results, {
      requestedGradeCode: searchFields.grade_code,
      cloneResults: this.isFlexibleDateSearch(searchFields),
    })

    if (hasExactResults) {
      this.hideLoadingSpinner()
      this.setSearchBusy(false)
      this.updatePartySummary(searchFields)
      return
    }

    const alternativeResults = await this.getAlternativeResultsFromBackend(searchFields, this.activeSearchController.signal)

    if (requestId !== this.latestSearchRequestId) {
      return
    }

    const hasAlternativeResults = this.showAlternativeCarousel(alternativeResults)

    this.hideLoadingSpinner()
    this.setSearchBusy(false)
    this.showNoResultsMessage()

    if (hasAlternativeResults) {
      this.showAlternativeResultsMessage()
    }

    this.updatePartySummary(searchFields)
  }

  async fetchAccommodationPageResults(fields = null) {
    const requestId = ++this.latestSearchRequestId
    this.cancelActiveSearch()
    this.activeSearchController = new AbortController()

    this.hideAccommodationExactResults()
    this.hideNoResultsMessage()
    this.hideAlternativeResultsMessage()
    this.showLoadingSpinner()

    const searchFields = this.withDefaultFields(fields ?? this.getFieldsFromForm())
    this.syncSearchFormFields(searchFields)
    this.currentSearchFields = searchFields
    this.updatePartySummary(searchFields)

    this.setSearchBusy(true)
    const results = await this.getResultsFromBackend(
      this.accommodationAvailabilityRequestFields(searchFields),
      this.activeSearchController.signal
    )

    if (requestId !== this.latestSearchRequestId) {
      return
    }

    const exactResults = this.accommodationExactResults(results)
    const hasExactResults = this.showAccommodationExactResults(exactResults)

    if (hasExactResults) {
      this.hideLoadingSpinner()
      this.setSearchBusy(false)
      this.updatePartySummary(searchFields)
      return
    }

    const alternativeResults = await this.getAlternativeResultsFromBackend({
      ...this.accommodationAvailabilityRequestFields(searchFields),
      id: this.currentAccommodationId(),
    }, this.activeSearchController.signal)

    if (requestId !== this.latestSearchRequestId) {
      return
    }

    const hasAlternativeResults = this.showAccommodationAlternativeResults(alternativeResults)

    this.hideLoadingSpinner()
    this.setSearchBusy(false)
    this.showNoResultsMessage()

    if (hasAlternativeResults) {
      this.showAlternativeResultsMessage()
    }

    this.updatePartySummary(searchFields)
  }

  updateSearchUrl(searchFields) {
    if (!this.isSearchResultsPage || !window.history?.pushState) {
      return
    }

    const params = new URLSearchParams()

    Object.entries(searchFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params.set(key, value)
      }
    })

    const queryString = params.toString()
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname

    if (nextUrl !== `${window.location.pathname}${window.location.search}`) {
      window.history.pushState(null, "", nextUrl)
    }
  }

  getFieldsFromQueryString() {
    const formFields = {}
    const params = new URLSearchParams(window.location.search)

    params.forEach((value, key) => {
      if (value !== "") {
        formFields[key] = value
      }
    })

    return this.withDefaultFields(formFields)
  }

  getFieldsFromForm() {
    const formData = new FormData(this.availabilitySearchBar)
    const formFields = {}

    formData.forEach((value, key) => {
      if (value !== "") {
        formFields[key] = value
      }
    })

    if (this.bookingType?.value) {
      formFields.booking_type = this.bookingType.value
    }

    if (this.isAccommodationPage && this.currentAccommodationId()) {
      formFields.id = String(this.currentAccommodationId())
    }

    return this.withDefaultFields(formFields)
  }

  withDefaultFields(formFields) {
    const normalizedFields = { ...formFields }
    const dogFriendly = this.isDogFriendlySearch(formFields)

    if (!formFields.booking_type) {
      normalizedFields.booking_type = "1"
    }

    if (!formFields.no_of_children) {
      normalizedFields.no_of_children = "0"
    }

    if (!formFields.date_flexibility) {
      normalizedFields.date_flexibility = "exact"
    }

    const dogs = this.clampNumber(formFields.no_of_pets, 0, 3, dogFriendly ? 1 : 0)

    normalizedFields.dog_friendly = dogs > 0 ? "1" : "0"
    normalizedFields.no_of_pets = String(dogs)

    return this.normalizeGuestFields(normalizedFields)
  }

  normalizeGuestFields(formFields) {
    const adults = this.clampNumber(formFields.no_of_adults, MIN_ADULTS, MAX_PEOPLE, MIN_ADULTS)
    const children = this.clampNumber(formFields.no_of_children, 0, Math.max(0, MAX_PEOPLE - adults), 0)
    const dogFriendly = this.isDogFriendlySearch(formFields)
    const dogs = this.clampNumber(formFields.no_of_pets, 0, 3, dogFriendly ? 1 : 0)

    return {
      ...formFields,
      no_of_adults: String(adults),
      no_of_children: String(children),
      no_of_pets: String(dogs),
      dog_friendly: dogs > 0 ? "1" : "0",
    }
  }

  isDogFriendlySearch(formFields = {}) {
    const dogs = parseInt(formFields.no_of_pets ?? "0", 10)

    if (!Number.isNaN(dogs) && dogs > 0) {
      return true
    }

    return ["1", 1, true, "true", "on", "yes"].includes(formFields.dog_friendly)
  }

  clampNumber(value, min, max, fallback = min) {
    const number = parseInt(value, 10)

    if (Number.isNaN(number)) {
      return fallback
    }

    return Math.min(max, Math.max(min, number))
  }

  syncSearchFormFields(searchFields) {
    ["no_of_adults", "no_of_children", "no_of_pets"].forEach((fieldName) => {
      this.availabilitySearchBar.querySelectorAll(`[name="${fieldName}"]`).forEach((field) => {
        if (field.value !== String(searchFields[fieldName])) {
          field.value = String(searchFields[fieldName])
        }
      })
    })

    this.availabilitySearchBar.querySelectorAll("[data-search-bar-dog-friendly-toggle]").forEach((field) => {
      field.checked = this.isDogFriendlySearch(searchFields)
    })

    window.dispatchEvent(new CustomEvent("search-bar-guests:normalize"))
  }

  updatePartySummary(searchFields) {
    if (!this.partySummary) {
      return
    }

    const adults = this.normalizedGuestCount(searchFields.no_of_adults, 1, 1, MAX_PEOPLE)
    const children = this.normalizedGuestCount(searchFields.no_of_children, 0, 0, Math.max(0, MAX_PEOPLE - adults))
    const dogs = this.normalizedGuestCount(searchFields.no_of_pets, 0, 0, 3)

    if (children === 0 && dogs === 0) {
      this.partySummary.innerHTML = ""
      this.partySummary.removeAttribute("aria-label")
      this.hidePartySummary()
      return
    }

    const parts = [
      this.formatGuestCount(adults, "Adult", "Adults"),
      children > 0 ? this.formatGuestCount(children, "Child", "Children") : null,
      dogs > 0 ? this.formatGuestCount(dogs, "Dog", "Dogs") : null,
    ].filter(Boolean)

    this.partySummary.innerHTML = `
      <span class="holiday-park-search-results__party-summary-label">Showing stays for</span>
      ${parts.map((part) => `<span class="holiday-park-search-results__party-summary-chip">${this.escapeHtml(part)}</span>`).join(" ")}
    `
    this.partySummary.setAttribute("aria-label", `Showing stays for ${parts.join(", ")}`)
    this.partySummary.classList.remove("hidden")
  }

  normalizedGuestCount(value, fallback = 0, minimum = 0, maximum = Number.MAX_SAFE_INTEGER) {
    const count = parseInt(value, 10)

    if (Number.isNaN(count)) {
      return fallback
    }

    return Math.min(maximum, Math.max(minimum, count))
  }

  formatGuestCount(count, singular, plural) {
    return `${count} ${count === 1 ? singular : plural}`
  }

  showResults(results, options = {}) {
    const normalizedResults = this.sortResultsByArrivalDate(this.normalizeResults(results))
    const gradeCodesAvailable = normalizedResults
      .map((result) => result.grade_code)
      .filter(Boolean)
    const trackedItems = []
    const listName = options.listName ?? (options.cloneResults ? LIST_NAME_ALTERNATIVE_DATES : LIST_NAME_SEARCH_RESULTS)

    let visibleResults = false

    this.clearAlternativeClones()

    this.propertyList.querySelectorAll(".accommodation-item").forEach((property) => {
      property.style.display = "none"
    })

    if (options.cloneResults) {
      normalizedResults.forEach((propertyResult) => {
        const sourceProperty = this.findPropertyForResult(propertyResult)

        if (
          !sourceProperty
          || !this.resultHasBookingDetails(propertyResult)
          || (options.requestedGradeCode && propertyResult.grade_code !== options.requestedGradeCode)
        ) {
          return
        }

        const property = this.createAlternativePropertyCard(sourceProperty)

        this.updatePropertyCard(property, propertyResult)
        this.addBookingInfoToButton(
          property.querySelector(".booking-button"),
          propertyResult.grade_code,
          propertyResult.accommodation_id ?? property.dataset.accommodation_id,
          propertyResult
        )
        const item = this.buildPropertyResultItem(property, propertyResult)

        trackedItems.push(item)
        this.decoratePropertyLinks(property, item, listName, trackedItems.length)
        property.style.display = null
        this.propertyCardContainer.appendChild(property)
        visibleResults = true
      })
    } else {
      this.propertyList.querySelectorAll(".accommodation-item").forEach((property) => {
        const gradeCode = property.dataset.grade_code
        const accommodationId = property.dataset.accommodation_id
        const propertyResult = normalizedResults.find((result) => result.grade_code === gradeCode)

        if (
          !gradeCode
          || !gradeCodesAvailable.includes(gradeCode)
          || !propertyResult
          || (options.requestedGradeCode && gradeCode !== options.requestedGradeCode)
        ) {
          property.style.display = "none"
          return
        }

        this.updatePropertyCard(property, propertyResult)

        if (this.resultHasBookingDetails(propertyResult)) {
          this.addBookingInfoToButton(property.querySelector(".booking-button"), gradeCode, accommodationId, propertyResult)
          const item = this.buildPropertyResultItem(property, propertyResult)

          trackedItems.push(item)
          this.decoratePropertyLinks(property, item, listName, trackedItems.length)
          property.style.display = null
          visibleResults = true
        } else {
          property.style.display = "none"
        }
      })
    }

    if (visibleResults) {
      this.showPropertyList()
      this.trackRenderedList(listName, trackedItems)
    }

    return visibleResults
  }

  accommodationAvailabilityRequestFields(searchFields) {
    const requestFields = { ...searchFields }

    delete requestFields.id
    delete requestFields.grade_code
    delete requestFields.alternative_search

    return requestFields
  }

  accommodationExactResults(results) {
    const linkedGradeCodes = new Set(this.linkedGradeCodes)

    return this.sortResultsByArrivalDate(this.normalizeResults(results))
      .filter((result) => linkedGradeCodes.has(result.grade_code))
      .filter((result) => this.resultHasBookingDetails(result))
  }

  showAccommodationExactResults(results) {
    if (!this.exactResultsSection || !this.exactResultsList) {
      return false
    }

    const trackedItems = []
    this.exactResultsList.innerHTML = ""

    if (results.length === 0) {
      this.hideAccommodationExactResults()
      return false
    }

    results.forEach((result) => {
      const card = this.createAccommodationExactResultCard(result)

      if (card) {
        trackedItems.push(this.buildAccommodationResultItem(result))
        this.exactResultsList.appendChild(card)
      }
    })

    if (!this.exactResultsList.children.length) {
      this.hideAccommodationExactResults()
      return false
    }

    const carousel = this.findExactResultsCarouselElements()
    this.bindExactResultsCarouselControls(carousel)
    window.requestAnimationFrame(() => this.syncExactResultsCarouselControls(carousel))
    this.exactResultsSection.classList.remove("hidden")
    this.trackRenderedList(LIST_NAME_ACCOMMODATION_OPTIONS, trackedItems)

    return true
  }

  hideAccommodationExactResults() {
    this.exactResultsSection?.classList.add("hidden")
    if (this.exactResultsList) {
      this.exactResultsList.innerHTML = ""
    }
  }

  createAccommodationExactResultCard(propertyResult) {
    const currentAccommodation = this.currentAccommodationData()
    const linkedProperty = this.linkedPropertiesByGradeCode.get(propertyResult.grade_code) ?? {}
    const optionLabel = linkedProperty.label ?? propertyResult.grade_code ?? "Available option"
    const berthCount = linkedProperty.no_of_berths
    const accommodationId = this.currentAccommodationId()
    const bookingBaseUrl = currentAccommodation?.booking_base_url

    if (!bookingBaseUrl || !accommodationId) {
      return null
    }

    const card = document.createElement("article")
    const bookingUrl = this.buildBookingUrlFromBaseUrl(
      bookingBaseUrl,
      propertyResult.grade_code,
      accommodationId,
      propertyResult
    )
    const imageMarkup = currentAccommodation.image_url
      ? `
        <div class="holiday-park-accommodation-search__result-image">
          <img src="${this.escapeHtml(currentAccommodation.image_url)}" alt="${this.escapeHtml(currentAccommodation.image_alt || currentAccommodation.title || "Holiday home")}" loading="lazy">
        </div>
      `
      : ""

    card.className = "holiday-park-accommodation-search__result-card"
    card.dataset.accommodationExactCarouselCard = "true"
    card.innerHTML = `
      ${imageMarkup}
      <div class="holiday-park-accommodation-search__result-meta">
        <span class="holiday-park-accommodation-search__result-option">${this.escapeHtml(optionLabel)}</span>
        ${berthCount ? `<span class="holiday-park-accommodation-search__result-berths">Sleeps ${this.escapeHtml(String(berthCount))}</span>` : ""}
      </div>
      <h4 class="holiday-park-accommodation-search__result-title">${this.escapeHtml(currentAccommodation.title ?? "Holiday home")}</h4>
      <div class="holiday-park-accommodation-search__result-stay">
        <span>${this.escapeHtml(this.formatDateForDisplay(propertyResult.arrival_date))}</span>
        <span>${this.escapeHtml(this.formatNights(propertyResult.no_of_nights))}</span>
      </div>
      <div class="holiday-park-accommodation-search__result-footer">
        <strong class="holiday-park-accommodation-search__result-price">${this.escapeHtml(this.formatFromPrice(propertyResult))}</strong>
        <a class="btn secondary-btn holiday-park-accommodation-search__result-button" href="${this.escapeHtml(bookingUrl.toString())}">Book now</a>
      </div>
    `

    const item = this.buildAccommodationResultItem(propertyResult)

    this.decorateTrackedLink(
      card.querySelector(".holiday-park-accommodation-search__result-button"),
      item,
      LIST_NAME_ACCOMMODATION_OPTIONS,
      this.exactResultsList?.children.length + 1 ?? 1
    )

    return card
  }

  showAccommodationAlternativeResults(results) {
    const carousel = this.findAlternativeCarouselElements()

    if (!carousel.track || !carousel.viewport) {
      return false
    }

    const normalizedResults = this.sortResultsByArrivalDate(this.normalizeResults(results))
    const trackedItems = []
    let visibleResults = false

    this.clearAlternativeCarousel()
    this.hideAccommodationExactResults()

    normalizedResults.forEach((propertyResult) => {
      if (!this.resultHasBookingDetails(propertyResult)) {
        return
      }

      const card = this.createAccommodationAlternativeCarouselCard(propertyResult)
      const dateButton = this.createAlternativeDateButton(propertyResult)

      if (!card) {
        return
      }

      trackedItems.push(this.buildAccommodationResultItem(propertyResult))
      carousel.track.appendChild(card)
      if (dateButton) {
        carousel.calendar?.appendChild(dateButton)
      }
      visibleResults = true
    })

    if (visibleResults) {
      this.bindAlternativeCarouselControls(carousel)
      window.requestAnimationFrame(() => this.centerNearestAlternative(carousel))
      this.trackRenderedList(LIST_NAME_ACCOMMODATION_ALTERNATIVES, trackedItems)
    }

    return visibleResults
  }

  showAlternativeCarousel(results) {
    const carousel = this.findAlternativeCarouselElements()

    if (!carousel.track || !carousel.viewport) {
      return this.showResults(results, { cloneResults: true, listName: LIST_NAME_ALTERNATIVE_DATES })
    }

    const normalizedResults = this.sortResultsByArrivalDate(this.normalizeResults(results))
    const trackedItems = []
    let visibleResults = false

    this.clearAlternativeCarousel()
    this.clearAlternativeClones()
    this.hidePropertyList()

    normalizedResults.forEach((propertyResult) => {
      const sourceProperty = this.findPropertyForResult(propertyResult)

      if (!sourceProperty || !this.resultHasBookingDetails(propertyResult)) {
        return
      }

      const card = this.createAlternativeCarouselCard(sourceProperty, propertyResult)
      const dateButton = this.createAlternativeDateButton(propertyResult)

      if (!card) {
        return
      }

      trackedItems.push(this.buildPropertyResultItem(sourceProperty, propertyResult))
      carousel.track.appendChild(card)
      if (dateButton) {
        carousel.calendar?.appendChild(dateButton)
      }
      visibleResults = true
    })

    if (visibleResults) {
      this.bindAlternativeCarouselControls(carousel)
      window.requestAnimationFrame(() => this.centerNearestAlternative(carousel))
      this.trackRenderedList(LIST_NAME_ALTERNATIVE_DATES, trackedItems)
    }

    return visibleResults
  }

  isFlexibleDateSearch(searchFields) {
    return DATE_FLEXIBILITY_PATTERN.test(searchFields.date_flexibility ?? "")
  }

  normalizeResults(results) {
    const resultList = Array.isArray(results) ? results : [results]

    return resultList
      .map((result) => result?.["@attributes"] ?? result)
      .filter(Boolean)
  }

  sortResultsByArrivalDate(results) {
    return [...results].sort((first, second) => {
      const firstDate = this.parseDate(first?.arrival_date)?.valueOf() ?? Number.MAX_SAFE_INTEGER
      const secondDate = this.parseDate(second?.arrival_date)?.valueOf() ?? Number.MAX_SAFE_INTEGER

      if (firstDate !== secondDate) {
        return firstDate - secondDate
      }

      return `${first?.grade_code ?? ""}${first?.accommodation_id ?? ""}`
        .localeCompare(`${second?.grade_code ?? ""}${second?.accommodation_id ?? ""}`)
    })
  }

  clearAlternativeClones() {
    this.propertyList?.querySelectorAll('[data-alternative-clone="true"]').forEach((property) => {
      property.remove()
    })
  }

  clearAlternativeCarousel() {
    const carousel = this.findAlternativeCarouselElements()

    carousel.track?.querySelectorAll("[data-alternative-carousel-card]").forEach((card) => {
      card.remove()
    })

    carousel.calendar?.querySelectorAll("[data-alternative-date-button]").forEach((button) => {
      button.remove()
    })
  }

  createAlternativePropertyCard(sourceProperty) {
    const property = sourceProperty.cloneNode(true)

    property.dataset.alternativeClone = "true"
    property.style.display = null
    property.removeAttribute("id")
    property.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"))

    return property
  }

  updatePropertyCard(property, propertyResult) {
    const price = property.querySelector(".booking-price")
    const arrivalDate = property.querySelector(".booking-arrival-date")
    const nights = property.querySelector(".booking-nights")

    if (nights && propertyResult.no_of_nights) {
      const noOfNights = propertyResult.no_of_nights
      nights.textContent = `${noOfNights} ${parseInt(noOfNights) === 1 ? "Night" : "Nights"}`
    }

    if (price && (propertyResult.from_price || propertyResult.price)) {
      price.textContent = this.formatFromPrice(propertyResult)
    }

    if (arrivalDate && propertyResult.arrival_date) {
      arrivalDate.textContent = this.formatFirstAvailableSummary(
        propertyResult.arrival_date,
        propertyResult.from_price
      )
    }
  }

  createAlternativeCarouselCard(sourceProperty, propertyResult) {
    const sourceButton = sourceProperty.querySelector(".booking-button")

    if (!sourceButton) {
      return null
    }

    const gradeCode = propertyResult.grade_code
    const accommodationId = propertyResult.accommodation_id ?? sourceProperty.dataset.accommodation_id
    const bookingUrl = this.buildBookingUrl(sourceButton, gradeCode, accommodationId, propertyResult)
    const detailsUrl = this.getPropertyDetailsUrl(sourceProperty)
    const title = this.getPropertyTitle(sourceProperty)
    const image = this.getPropertyImage(sourceProperty)
    const card = document.createElement("article")

    card.className = "holiday-park-alternative-card"
    card.dataset.alternativeCarouselCard = "true"
    const arrivalDate = this.formatDateForQuery(propertyResult.arrival_date) ?? ""

    card.dataset.arrivalDate = arrivalDate
    card.dataset.alternativeDate = arrivalDate

    const imageMarkup = image
      ? `<img src="${this.escapeHtml(image.src)}" alt="${this.escapeHtml(image.alt || title)}" loading="lazy">`
      : `<div class="holiday-park-alternative-card__image-placeholder" aria-hidden="true"></div>`
    const linkedImageMarkup = detailsUrl
      ? `<a class="holiday-park-alternative-card__image-link" href="${this.escapeHtml(detailsUrl)}" aria-label="View details for ${this.escapeHtml(title)}">${imageMarkup}</a>`
      : imageMarkup

    const requestedBadge = this.isRequestedAccommodation(propertyResult)
      ? `<span class="holiday-park-alternative-card__badge">Requested accommodation</span>`
      : ""
    const titleMarkup = detailsUrl
      ? `<a class="holiday-park-alternative-card__title-link" href="${this.escapeHtml(detailsUrl)}">${this.escapeHtml(title)}</a>`
      : this.escapeHtml(title)
    const detailsLink = detailsUrl
      ? `<a class="btn primary-btn holiday-park-alternative-card__details" href="${this.escapeHtml(detailsUrl)}">More details</a>`
      : ""

    card.innerHTML = `
      <div class="holiday-park-alternative-card__image">
        ${linkedImageMarkup}
      </div>
      <div class="holiday-park-alternative-card__body">
        <div class="holiday-park-alternative-card__meta">
          <span class="holiday-park-alternative-card__date">${this.escapeHtml(this.formatDateForDisplay(propertyResult.arrival_date))}</span>
          ${requestedBadge}
        </div>
        <h4 class="holiday-park-alternative-card__title">${titleMarkup}</h4>
        <div class="holiday-park-alternative-card__stay">
          <span>${this.escapeHtml(this.formatNights(propertyResult.no_of_nights))}</span>
          <strong>${this.escapeHtml(this.formatFromPrice(propertyResult))}</strong>
        </div>
        <div class="holiday-park-alternative-card__actions">
          ${detailsLink}
          <a class="btn secondary-btn holiday-park-alternative-card__button" href="${this.escapeHtml(bookingUrl.toString())}">Book</a>
        </div>
      </div>
    `

    const item = this.buildPropertyResultItem(sourceProperty, propertyResult)

    this.decorateTrackedLink(
      card.querySelector(".holiday-park-alternative-card__button"),
      item,
      LIST_NAME_ALTERNATIVE_DATES,
      card.parentElement?.children.length + 1 ?? 1
    )
    this.decorateTrackedLink(
      card.querySelector(".holiday-park-alternative-card__details"),
      item,
      LIST_NAME_ALTERNATIVE_DATES,
      card.parentElement?.children.length + 1 ?? 1
    )

    return card
  }

  createAccommodationAlternativeCarouselCard(propertyResult) {
    const metadata = this.accommodationMetadataForResult(propertyResult)

    if (!metadata?.booking_base_url) {
      return null
    }

    const bookingUrl = this.buildBookingUrlFromBaseUrl(
      metadata.booking_base_url,
      propertyResult.grade_code,
      propertyResult.accommodation_id ?? metadata.accommodation_id,
      propertyResult
    )
    const card = document.createElement("article")
    const arrivalDate = this.formatDateForQuery(propertyResult.arrival_date) ?? ""
    const isSelectedAccommodation = Number(propertyResult.accommodation_id) === this.currentAccommodationId()
    const requestedBadge = isSelectedAccommodation
      ? `<span class="holiday-park-alternative-card__badge">Selected accommodation</span>`
      : ""
    const imageMarkup = metadata.image_url
      ? `<img src="${this.escapeHtml(metadata.image_url)}" alt="${this.escapeHtml(metadata.image_alt || metadata.title)}" loading="lazy">`
      : `<div class="holiday-park-alternative-card__image-placeholder" aria-hidden="true"></div>`
    const linkedImageMarkup = metadata.details_url
      ? `<a class="holiday-park-alternative-card__image-link" href="${this.escapeHtml(metadata.details_url)}" aria-label="View details for ${this.escapeHtml(metadata.title)}">${imageMarkup}</a>`
      : imageMarkup
    const titleMarkup = metadata.details_url
      ? `<a class="holiday-park-alternative-card__title-link" href="${this.escapeHtml(metadata.details_url)}">${this.escapeHtml(metadata.title)}</a>`
      : this.escapeHtml(metadata.title)
    const detailsLink = metadata.details_url
      ? `<a class="btn primary-btn holiday-park-alternative-card__details" href="${this.escapeHtml(metadata.details_url)}">More details</a>`
      : ""

    card.className = "holiday-park-alternative-card"
    card.dataset.alternativeCarouselCard = "true"
    card.dataset.arrivalDate = arrivalDate
    card.dataset.alternativeDate = arrivalDate
    card.innerHTML = `
      <div class="holiday-park-alternative-card__image">
        ${linkedImageMarkup}
      </div>
      <div class="holiday-park-alternative-card__body">
        <div class="holiday-park-alternative-card__meta">
          <span class="holiday-park-alternative-card__date">${this.escapeHtml(this.formatDateForDisplay(propertyResult.arrival_date))}</span>
          ${requestedBadge}
        </div>
        <h4 class="holiday-park-alternative-card__title">${titleMarkup}</h4>
        <div class="holiday-park-alternative-card__stay">
          <span>${this.escapeHtml(this.formatNights(propertyResult.no_of_nights))}</span>
          <strong>${this.escapeHtml(this.formatFromPrice(propertyResult))}</strong>
        </div>
        <div class="holiday-park-alternative-card__actions">
          ${detailsLink}
          <a class="btn secondary-btn holiday-park-alternative-card__button" href="${this.escapeHtml(bookingUrl.toString())}">Book</a>
        </div>
      </div>
    `

    const item = this.buildAccommodationResultItem(propertyResult)

    this.decorateTrackedLink(
      card.querySelector(".holiday-park-alternative-card__button"),
      item,
      LIST_NAME_ACCOMMODATION_ALTERNATIVES,
      card.parentElement?.children.length + 1 ?? 1
    )
    this.decorateTrackedLink(
      card.querySelector(".holiday-park-alternative-card__details"),
      item,
      LIST_NAME_ACCOMMODATION_ALTERNATIVES,
      card.parentElement?.children.length + 1 ?? 1
    )

    return card
  }

  createAlternativeDateButton(propertyResult) {
    const date = this.parseDate(propertyResult.arrival_date)
    const isoDate = this.formatDateForQuery(propertyResult.arrival_date)

    if (!date || !isoDate) {
      return null
    }

    const button = document.createElement("button")

    button.type = "button"
    button.className = "holiday-park-alternative-date"
    button.dataset.alternativeDateButton = "true"
    button.dataset.alternativeDate = isoDate
    button.setAttribute("aria-label", `Show ${date.format("dddd Do MMMM YYYY")}`)
    button.innerHTML = `
      <span class="holiday-park-alternative-date__weekday">${this.escapeHtml(date.format("ddd"))}</span>
      <span class="holiday-park-alternative-date__day">${this.escapeHtml(date.format("D"))}</span>
      <span class="holiday-park-alternative-date__month">${this.escapeHtml(date.format("MMM"))}</span>
    `

    return button
  }

  buildBookingUrl(button, gradeCode, accommodationId, propertyResult = {}) {
    return this.buildBookingUrlFromBaseUrl(
      button.getAttribute("href"),
      gradeCode,
      accommodationId,
      propertyResult
    )
  }

  buildBookingUrlFromBaseUrl(baseUrl, gradeCode, accommodationId, propertyResult = {}) {
    const url = new URL(baseUrl, window.location.origin)
    const formFields = { ...this.currentSearchFields }
    const selectedArrivalDate = this.formatDateForQuery(propertyResult.arrival_date)

    formFields.id = accommodationId
    if (selectedArrivalDate && formFields.arrival_date && selectedArrivalDate !== formFields.arrival_date) {
      formFields.alternative_source_arrival_date = formFields.alternative_source_arrival_date ?? formFields.arrival_date
    }
    formFields.arrival_date = selectedArrivalDate ?? formFields.arrival_date
    formFields.no_of_nights = propertyResult.no_of_nights ?? formFields.no_of_nights
    delete formFields.alternative_search
    delete formFields.dog_friendly
    url.search = new URLSearchParams(formFields)
    url.searchParams.set("grade_code", gradeCode)

    return url
  }

  addBookingInfoToButton(button, gradeCode, accommodationId, propertyResult = {}) {
    if (!button) {
      return
    }

    const url = this.buildBookingUrl(button, gradeCode, accommodationId, propertyResult)
    button.setAttribute("href", url.toString())
  }

  trackRenderedList(listName, items) {
    const filteredItems = items.filter((item) => Boolean(item?.item_id || item?.item_name))

    if (filteredItems.length === 0) {
      return
    }

    trackViewItemList({
      listName,
      items: filteredItems,
    })
  }

  decorateTrackedLink(link, item, listName, index) {
    if (!link) {
      return
    }

    link.dataset.ga4SelectItem = JSON.stringify(item)
    link.dataset.ga4SelectItemList = listName
    link.dataset.ga4SelectItemIndex = String(index)
  }

  decoratePropertyLinks(property, item, listName, index) {
    this.findPropertyCtas(property).forEach((link) => {
      this.decorateTrackedLink(link, item, listName, index)
    })
  }

  findPropertyCtas(property) {
    return Array.from(property.querySelectorAll("a[href]")).filter((link) => {
      const label = link.textContent?.trim().toLowerCase()

      return label === "book now" || label === "more details"
    })
  }

  buildPropertyResultItem(property, propertyResult = {}) {
    return buildAccommodationItem({
      itemId: propertyResult.grade_code ?? property.dataset.grade_code,
      itemName: property.dataset.accommodationTitle ?? this.getPropertyTitle(property),
      itemCategory: property.dataset.accommodationCategory ?? "Holiday Park Accommodation",
      price: propertyResult.price ?? 0,
      quantity: 1,
    })
  }

  buildAccommodationResultItem(propertyResult = {}) {
    const metadata = this.accommodationMetadataForResult(propertyResult) ?? this.currentAccommodationData()

    return buildAccommodationItem({
      itemId: propertyResult.grade_code ?? metadata.accommodation_id ?? "holiday-park-option",
      itemName: metadata.title ?? "Holiday home",
      itemCategory: "Holiday Park Accommodation",
      price: propertyResult.price ?? 0,
      quantity: 1,
    })
  }

  getPropertyTitle(property) {
    return property.querySelector(".room-description h2, .description h3, .inner-description h3, h2.h3, h3")?.textContent?.trim() || "Holiday home"
  }

  getPropertyImage(property) {
    const image = property.querySelector(".image-container img")

    if (!image) {
      return null
    }

    return {
      src: image.currentSrc || image.src,
      alt: image.alt || "",
    }
  }

  getPropertyDetailsUrl(property) {
    const datasetUrl = property.dataset.detailsUrl

    if (datasetUrl) {
      return new URL(datasetUrl, window.location.origin).toString()
    }

    const detailsLink = [...property.querySelectorAll("a[href]")]
      .find((link) => link.textContent?.trim().toLowerCase() === "more details")

    if (!detailsLink) {
      return null
    }

    return new URL(detailsLink.getAttribute("href"), window.location.origin).toString()
  }

  currentAccommodationId() {
    return Number(this.currentAccommodationData()?.accommodation_id ?? 0)
  }

  currentAccommodationData() {
    return this.accommodationPageData.current_accommodation ?? {}
  }

  accommodationMetadataForResult(propertyResult) {
    const accommodationId = Number(propertyResult?.accommodation_id ?? 0)

    if (accommodationId && accommodationId === this.currentAccommodationId()) {
      return this.currentAccommodationData()
    }

    return this.alternativeAccommodations[accommodationId] ?? null
  }

  formatNights(noOfNights) {
    const nights = parseInt(noOfNights)

    if (!nights) {
      return ""
    }

    return `${nights} ${nights === 1 ? "night" : "nights"}`
  }

  formatPrice(price) {
    return price ? `£${price}` : ""
  }

  formatFromPrice(propertyResult) {
    const fromPrice = propertyResult?.from_price

    return fromPrice
      ? `From ${this.formatPrice(fromPrice)}`
      : this.formatPrice(propertyResult?.price)
  }

  formatFirstAvailableSummary(date, price) {
    const formattedDate = this.formatDateForDisplay(date)
    const formattedPrice = this.formatPrice(price)

    if (!formattedDate) {
      return ""
    }

    return formattedPrice
      ? `First available check-in ${formattedDate} · From ${formattedPrice}`
      : `First available check-in ${formattedDate}`
  }

  isRequestedAccommodation(propertyResult) {
    return propertyResult.is_requested_accommodation === true
      || propertyResult.is_requested_accommodation === 1
      || propertyResult.is_requested_accommodation === "1"
      || propertyResult.is_requested_accommodation === "true"
  }

  findAlternativeCarouselElements() {
    const root = this.alternativeResultsMessage

    return {
      root,
      calendar: root?.querySelector("[data-alternative-calendar]"),
      viewport: root?.querySelector("[data-alternative-carousel-viewport]"),
      track: root?.querySelector("[data-alternative-carousel-track]"),
      previous: root?.querySelector("[data-alternative-carousel-prev]"),
      next: root?.querySelector("[data-alternative-carousel-next]"),
    }
  }

  findExactResultsCarouselElements() {
    const root = this.exactResultsSection?.querySelector("[data-accommodation-exact-carousel]")

    return {
      root,
      viewport: root?.querySelector("[data-accommodation-exact-carousel-viewport]"),
      previous: root?.querySelector("[data-accommodation-exact-carousel-prev]"),
      next: root?.querySelector("[data-accommodation-exact-carousel-next]"),
    }
  }

  bindExactResultsCarouselControls(carousel) {
    if (!carousel.viewport || carousel.root?.dataset.accommodationExactCarouselBound === "true") {
      return
    }

    carousel.root.dataset.accommodationExactCarouselBound = "true"

    carousel.previous?.addEventListener("click", () => {
      this.scrollExactResultsCarousel(carousel.viewport, -1)
    })

    carousel.next?.addEventListener("click", () => {
      this.scrollExactResultsCarousel(carousel.viewport, 1)
    })
  }

  syncExactResultsCarouselControls(carousel) {
    if (!carousel.viewport) {
      return
    }

    carousel.viewport.scrollLeft = 0
    const hasOverflow = carousel.viewport.scrollWidth > carousel.viewport.clientWidth + 4

    if (carousel.previous) {
      carousel.previous.hidden = !hasOverflow
    }

    if (carousel.next) {
      carousel.next.hidden = !hasOverflow
    }
  }

  scrollExactResultsCarousel(viewport, direction) {
    const card = viewport.querySelector("[data-accommodation-exact-carousel-card]")
    const scrollDistance = card ? card.getBoundingClientRect().width + 16 : viewport.clientWidth * 0.8

    viewport.scrollBy({
      left: scrollDistance * direction,
      behavior: "smooth",
    })
  }

  bindAlternativeCarouselControls(carousel) {
    if (!carousel.viewport || carousel.root?.dataset.alternativeCarouselBound === "true") {
      return
    }

    carousel.root.dataset.alternativeCarouselBound = "true"

    carousel.previous?.addEventListener("click", () => {
      this.scrollAlternativeCarousel(carousel.viewport, -1)
    })

    carousel.next?.addEventListener("click", () => {
      this.scrollAlternativeCarousel(carousel.viewport, 1)
    })

    carousel.root?.addEventListener("click", (event) => {
      const dateButton = event.target.closest("[data-alternative-date-button]")

      if (!dateButton || !carousel.root.contains(dateButton)) {
        return
      }

      this.scrollAlternativeToDate(dateButton.dataset.alternativeDate, carousel)
      this.scrollPanelToAlternativeBookings(carousel)
    })

    carousel.viewport.addEventListener("scroll", () => {
      window.cancelAnimationFrame(this.alternativeScrollFrame)
      this.alternativeScrollFrame = window.requestAnimationFrame(() => {
        this.updateActiveAlternativeFromCarousel(this.findAlternativeCarouselElements())
      })
    }, { passive: true })
  }

  scrollAlternativeCarousel(viewport, direction) {
    const card = viewport.querySelector("[data-alternative-carousel-card]")
    const scrollDistance = card ? card.getBoundingClientRect().width + 16 : viewport.clientWidth * 0.8

    viewport.scrollBy({
      left: scrollDistance * direction,
      behavior: "smooth",
    })
  }

  scrollAlternativeToDate(date, carousel = this.findAlternativeCarouselElements()) {
    if (!date || !carousel.track) {
      return
    }

    const card = carousel.track.querySelector(`[data-alternative-date="${this.escapeSelectorValue(date)}"]`)

    if (!card) {
      return
    }

    card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    this.setActiveAlternativeDate(date, carousel)
  }

  scrollPanelToAlternativeBookings(carousel = this.findAlternativeCarouselElements()) {
    const target = carousel.viewport ?? carousel.track
    const panelContent = this.container.querySelector(".holiday-park-accommodation-search__panel-content")

    if (!target) {
      return
    }

    if (!panelContent) {
      target.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" })
      return
    }

    const panelRect = panelContent.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    const targetTop = panelContent.scrollTop + targetRect.top - panelRect.top - 16

    panelContent.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    })
  }

  centerNearestAlternative(carousel) {
    const requestedDate = this.parseDate(this.currentSearchFields.arrival_date)
    const cards = [...(carousel.track?.querySelectorAll("[data-alternative-carousel-card]") ?? [])]

    if (!requestedDate || cards.length === 0) {
      cards[0]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
      this.setActiveAlternativeDate(cards[0]?.dataset.alternativeDate, carousel)
      return
    }

    const nearestCard = cards.reduce((nearest, card) => {
      const cardDate = this.parseDate(card.dataset.arrivalDate)

      if (!cardDate) {
        return nearest
      }

      const distance = Math.abs(cardDate.diff(requestedDate, "days"))
      const nearestDistance = nearest.date ? Math.abs(nearest.date.diff(requestedDate, "days")) : Number.MAX_SAFE_INTEGER

      if (distance < nearestDistance) {
        return { card, date: cardDate }
      }

      return nearest
    }, { card: cards[0], date: this.parseDate(cards[0]?.dataset.arrivalDate) }).card

    nearestCard?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    this.setActiveAlternativeDate(nearestCard?.dataset.alternativeDate, carousel)
  }

  updateActiveAlternativeFromCarousel(carousel) {
    const centeredCard = this.findCenteredAlternativeCard(carousel)

    if (centeredCard) {
      this.setActiveAlternativeDate(centeredCard.dataset.alternativeDate, carousel)
    }
  }

  findCenteredAlternativeCard(carousel) {
    const viewportRect = carousel.viewport?.getBoundingClientRect()
    const cards = [...(carousel.track?.querySelectorAll("[data-alternative-carousel-card]") ?? [])]

    if (!viewportRect || cards.length === 0) {
      return null
    }

    const viewportCenter = viewportRect.left + (viewportRect.width / 2)

    return cards.reduce((nearest, card) => {
      const rect = card.getBoundingClientRect()
      const distance = Math.abs((rect.left + rect.width / 2) - viewportCenter)

      if (!nearest || distance < nearest.distance) {
        return { card, distance }
      }

      return nearest
    }, null)?.card ?? null
  }

  setActiveAlternativeDate(date, carousel = this.findAlternativeCarouselElements()) {
    if (!date || !carousel.calendar) {
      return
    }

    carousel.calendar.querySelectorAll("[data-alternative-date-button]").forEach((button) => {
      const isActive = button.dataset.alternativeDate === date

      button.classList.toggle("is-active", isActive)
      if (isActive) {
        button.setAttribute("aria-current", "date")
        button.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
      } else {
        button.removeAttribute("aria-current")
      }
    })
  }

  resultHasBookingDetails(propertyResult) {
    return Boolean(
      propertyResult?.arrival_date
      && propertyResult?.price
      && propertyResult?.no_of_nights
    )
  }

  findPropertyForResult(propertyResult) {
    if (!this.propertyList) {
      return null
    }

    const gradeCode = propertyResult?.grade_code
    const accommodationId = propertyResult?.accommodation_id

    if (!gradeCode) {
      return null
    }

    if (accommodationId) {
      const matchingProperty = this.propertyList.querySelector(
        `.accommodation-item[data-grade_code="${this.escapeSelectorValue(gradeCode)}"][data-accommodation_id="${this.escapeSelectorValue(accommodationId)}"]`
      )

      if (matchingProperty) {
        return matchingProperty
      }

      const matchingAccommodation = this.propertyList.querySelector(
        `.accommodation-item[data-accommodation_id="${this.escapeSelectorValue(accommodationId)}"]`
      )

      if (matchingAccommodation) {
        return matchingAccommodation
      }
    }

    return this.propertyList.querySelector(`.accommodation-item[data-grade_code="${this.escapeSelectorValue(gradeCode)}"]`)
  }

  escapeSelectorValue(value) {
    if (window.CSS?.escape) {
      return CSS.escape(String(value))
    }

    return String(value).replace(/["\\]/g, "\\$&")
  }

  formatDateForDisplay(date) {
    if (!date) {
      return ""
    }

    return this.parseDate(date)?.format("Do MMMM YYYY") ?? date
  }

  formatDateForQuery(date) {
    return this.parseDate(date)?.format("YYYY-MM-DD") ?? null
  }

  escapeHtml(value) {
    const div = document.createElement("div")
    div.textContent = value ?? ""

    return div.innerHTML
  }

  parseDate(date) {
    const formats = ["YYYY-MM-DD", "MM/DD/YY"]
    const parsedDate = moment(date, formats, true)

    return parsedDate.isValid() ? parsedDate : null
  }

  async getResultsFromBackend(formFields, signal = null) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const queryRoute = this.getRoute("query")

      if (!queryRoute) {
        throw new Error("Missing holiday park availability route")
      }

      const response = await axios.post(queryRoute, formFields, {
        signal,
        headers: {
          ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
        },
      })

      return response.data?.availability?.data?.Result ?? []
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Holiday park availability search failed", error)
      }
      return []
    }
  }

  async getAlternativeResultsFromBackend(formFields, signal = null) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const alternativeRoute = this.getRoute("alternative")

      if (!alternativeRoute) {
        throw new Error("Missing holiday park alternative availability route")
      }

      const response = await axios.post(alternativeRoute, formFields, {
        signal,
        headers: {
          ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
        },
      })

      return response.data?.alternatives ?? []
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Holiday park alternative availability search failed", error)
      }
      return []
    }
  }

  getRoute(type) {
    return this.container.dataset[`${type}Route`]
      ?? this.container.dataset[`${type}_route`]
      ?? this.container.getAttribute(`data-${type}-route`)
      ?? this.container.getAttribute(`data-${type}_route`)
  }

  cancelActiveSearch() {
    this.activeSearchController?.abort()
    this.activeSearchController = null
  }

  setSearchBusy(isBusy) {
    this.availabilitySearchBar?.setAttribute('aria-busy', isBusy ? 'true' : 'false')

    const submitButton = this.availabilitySearchBar?.querySelector(".search-bar-submit, button[type='submit']")
    if (submitButton) {
      submitButton.disabled = isBusy
    }
  }

  hidePropertyList() {
    this.propertyList?.classList.add("hidden")
  }

  showPropertyList() {
    this.propertyList?.classList.remove("hidden")
  }

  hideLoadingSpinner() {
    this.loadingSpinner?.classList.add("hidden")
  }

  showLoadingSpinner() {
    this.loadingSpinner?.classList.remove("hidden")
  }

  hideNoResultsMessage() {
    this.noResultsMessage?.classList.remove("flex")
    this.noResultsMessage?.classList.add("hidden")
  }

  showNoResultsMessage() {
    this.noResultsMessage?.classList.remove("hidden")
    this.noResultsMessage?.classList.add("flex")
  }

  hidePartySummary() {
    this.partySummary?.classList.add("hidden")
  }

  hideAlternativeResultsMessage() {
    this.alternativeResultsMessage?.classList.add("hidden")
    this.clearAlternativeCarousel()
  }

  showAlternativeResultsMessage() {
    this.alternativeResultsMessage?.classList.remove("hidden")
  }
}

export default function initHolidayParkSearchResults() {
  document.querySelectorAll("[data-holiday-park-search-results], [data-holiday-park-accommodation-search], #search-results").forEach((container) => {
    if (container.dataset.holidayParkSearchResultsInitialized === "true") {
      return
    }

    container.dataset.holidayParkSearchResultsInitialized = "true"
    new HolidayParkSearchResults(container)
  })
}
